import React from 'react';
import {
	useDispatch,
} from 'react-redux';

import type CK from '~/types/contentking';

import NavigationLayoutToggle from '~/components/app/NavigationLayoutToggle';
import NavigationMinimizeToggle from '~/components/app/NavigationMinimizeToggle';

import {
	buildNewSecondaryItems,
} from './builders/buildNewSecondaryItems';
import useNewPrimaryItems from './builders/useNewPrimaryItems';

import {
	menu,
} from '~/actions';

import {
	type messages
} from './MainNavigation';
import useAutoExpandNavigation from '~/hooks/useAutoExpandNavigation';



type Props = {
	accountId: CK.AccountId | null,
	areSecondaryNavItemsHidden: boolean,
	areTechnicalSubItemsHidden: boolean,
	effectiveHomeAccountId: CK.AccountId | null,
	hasMultipleAccounts: boolean,
	isContentExpanded: boolean,
	isCurrentlyPhase2: boolean,
	isInternalExpanded: boolean,
	isPerformanceExpanded: boolean,
	isTechnicalExpanded: boolean,
	kingdomAdminFeatures: {
		areVisible: boolean,
	},
	messages: typeof messages,
	openSwitchAccountModal: () => void,
	selectedAccountId: CK.AccountId | null,
	setAreSecondaryNavItemsHidden: (hidden: boolean) => void,
	setAreTechnicalSubItemsHidden: (hidden: boolean) => void,
	setContentExpanded: (expanded: boolean) => void,
	setInternalExpanded: (expanded: boolean) => void,
	setPerformanceExpanded: (expanded: boolean) => void,
	setSelectedAccountId: (accountId: CK.AccountId | null) => void,
	setTechnicalExpanded: (expanded: boolean) => void,
	showPhase2CompactLayout: boolean,
	showToggle: boolean,
	technicalSectionItems: Array<React.ReactElement>,
	websiteAccountName: string | null,
	websiteId: CK.WebsiteId | null,
	websitesNavToggleRef: React.RefObject<HTMLLIElement>,
};

/**
 * New Navigation Component
 *
 * Renders the new (dark + green) navigation layout structure.
 * This component builds navigation items and handles the wrapper, list structure, and toggle buttons.
 */
const NewNavigation: React.FC<Props> = (props) => {
	const {
		accountId,
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		effectiveHomeAccountId,
		hasMultipleAccounts,
		isContentExpanded,
		isCurrentlyPhase2,
		isInternalExpanded,
		isPerformanceExpanded,
		isTechnicalExpanded,
		kingdomAdminFeatures,
		messages,
		openSwitchAccountModal,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setContentExpanded,
		setInternalExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		showToggle,
		technicalSectionItems,
		websiteId,
	} = props;

	const dispatch = useDispatch();
	const handleAutoExpandNavigation = useAutoExpandNavigation();

	// Click handlers for Phase 2 compact layout icons
	const handleWebsitesIconClick = React.useCallback(() => {
		// 1. Expand navigation to Phase 3 first
		handleAutoExpandNavigation();
		// Ensure Sub-items are visible (in case they were hidden)
		setAreTechnicalSubItemsHidden(false);
		setAreSecondaryNavItemsHidden(false);

		// 2. Collapse websites dropdown so its contents are visible (search box and websites list)
		// Set selectedAccountId to show the inline websites section
		setSelectedAccountId(effectiveHomeAccountId);
		// Also open the old dropdown state for consistency
		dispatch(menu.openWebsiteSwitcher());
	}, [handleAutoExpandNavigation, dispatch, effectiveHomeAccountId, setSelectedAccountId, setAreTechnicalSubItemsHidden, setAreSecondaryNavItemsHidden]);

	const handleSearchIconClick = React.useCallback(() => {
		// 1. Expand navigation to Phase 3 first
		handleAutoExpandNavigation();
		// Ensure Sub-items are visible (in case they were hidden)
		setAreTechnicalSubItemsHidden(false);
		setAreSecondaryNavItemsHidden(false);

		// 2. Collapse websites dropdown so its contents are visible (search box and websites list)
		// Set selectedAccountId to show the inline websites section
		setSelectedAccountId(effectiveHomeAccountId);
		// Also open the old dropdown state for consistency
		dispatch(menu.openWebsiteSwitcher());
	}, [handleAutoExpandNavigation, dispatch, effectiveHomeAccountId, setSelectedAccountId, setAreTechnicalSubItemsHidden, setAreSecondaryNavItemsHidden]);

	// Collapse all expandables when clicking a standalone item (so only that item shows green)
	const collapseAllExpandables = React.useCallback(() => {
		setContentExpanded(false);
		setPerformanceExpanded(false);
		setTechnicalExpanded(false);
		setInternalExpanded(false);
	}, [setContentExpanded, setPerformanceExpanded, setTechnicalExpanded, setInternalExpanded]);

	// Build primary navigation items using hook
	const newPrimaryItems = useNewPrimaryItems({
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		collapseAllExpandables,
		handleAutoExpandNavigation,
		handleSearchIconClick,
		handleWebsitesIconClick,
		hasMultipleAccounts,
		isContentExpanded,
		isCurrentlyPhase2,
		isPerformanceExpanded,
		isTechnicalExpanded,
		messages,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setContentExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		technicalSectionItems,
	});

	// Build secondary navigation items
	// When any expandable is expanded, suppress route-based active state so only the expanded header shows green
	const isAnyExpandableExpanded = isContentExpanded || isPerformanceExpanded || isTechnicalExpanded || isInternalExpanded;
	const newSecondaryItems = buildNewSecondaryItems({
		accountId,
		collapseAllExpandables,
		effectiveHomeAccountId,
		isAnyExpandableExpanded,
		isInternalExpanded,
		kingdomAdminFeatures,
		messages,
		onInternalToggle: () => {
			handleAutoExpandNavigation();
			const willExpand = !isInternalExpanded;
			setInternalExpanded(willExpand);
			if (willExpand) {
				setContentExpanded(false);
				setPerformanceExpanded(false);
				setTechnicalExpanded(false);
			}
		},
		openSwitchAccountModal,
		websiteId,
	});

	// Combine all navigation items into one list
	const allNavItems = [
		...newPrimaryItems,
		// Integrations, Settings, Account (moved here from secondary)
		...newSecondaryItems,
	];

	return (
		<>
			<div className="main-nav__wrapper">
				<nav
					aria-label="Main navigation"
					className="main-nav"
					role="navigation"
				>
					<ul className={areSecondaryNavItemsHidden && !isCurrentlyPhase2 ? 'main-nav__list main-nav__list--hide-secondary-items' : 'main-nav__list'}>
						{allNavItems.map((item, index) => {
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
							if (!item) {
								return null;
							}

							return (
								<React.Fragment key={`new-nav-item-${index}`}>
									{item}
								</React.Fragment>
							);
						})}
					</ul>
				</nav>
				{/* Toggle button at the very bottom of navbar */}
				{/* Hide divider when account list is shown (avoids double divider below Accounts Overview) */}
				{showToggle && !(areSecondaryNavItemsHidden && !isCurrentlyPhase2) && (
					<div className="main-nav__divider-wrapper">
						<div className="main-nav__divider" />
					</div>
				)}
				{showToggle && (
					<div className="sidebar__navigation-layout-toggle">
						<NavigationLayoutToggle />
					</div>
				)}
				{/* Minimize/Keep open toggle button - only for new navigation */}
				<div className="sidebar__navigation-minimize-toggle">
					<NavigationMinimizeToggle />
				</div>
			</div>
		</>
	);
};

export default NewNavigation;
